<script setup name="AdminScheduleRecipientList" lang="ts">
import { computed, onMounted, reactive, ref } from 'vue';

import { FREQUENCY_TYPE_TO_SUCCESS_MESSAGE } from '~/constants';
import { useProfileStore, useSnackbarStore } from '~/store';

import { useShowModal } from '~/features/useModalsProvider';
import { useService } from '~/features/useService.js';
import { getIdsFromArray, getKeyFromSearch } from '~/features/useUrlSearch';
import { capitalize, isEmpty } from '~/features/useUtilities';

import Service from '~/services/Service.js';

import AdminScheduleRecipientListFilters from '~/components/AdminScheduleRecipientListFilters.vue';
import BaseButton from '~/components/BaseButton.vue';
import BasePage from '~/components/BasePage.vue';
import CustomCard from '~/components/CustomCard.vue';
import DataTable from '~/components/DataTable.vue';
import DataTableActions from '~/components/DataTableActions.vue';
import SearchField from '~/components/SearchField.vue';

import type { IDataTableSettings, IOption } from '~/types';
import type { IRecipientLisRow } from '~/types/adminRecipientList';

import DataTableLoading from './DataTableLoading.vue';
import ModalConfirmDanger from './ModalConfirmDanger.vue';
import ServiceCard from './ServiceCard.vue';

const showModal = useShowModal();
const snackbar = useSnackbarStore();
const profileStore = useProfileStore();
const isSpectator = computed(() => profileStore.isSpectator);

interface IQuery {
  search: string | null;
}

const tableRows = ref<IRecipientLisRow[]>([]);
const tradelanes = ref([]);

const query = reactive<IQuery>({
  search: getKeyFromSearch('search'),
});

const filtersQuery = reactive<{
  trades: IOption[];
}>({
  trades: [],
});

const serviceState = useService(
  Service.recipientList().index(),
  ({ data }: { data: IRecipientLisRow[] }) => {
    if (isEmpty(data)) {
      return false;
    }

    tableRows.value = data;
    return true;
  },
  {
    loading: 'skeleton',
    cancelOnNewRequest: true,
    paginated: true,
    query: computed(() => ({
      search: query.search,
      trades: filtersQuery.trades,
    })),
    getQuery: () => ({
      search: query.search,
      tradelanes: getIdsFromArray(filtersQuery.trades),
    }),
  },
);

const tableSettings: IDataTableSettings<IRecipientLisRow> = {
  editable: false,
  pagination: true,
  columns: [
    { header: 'List name', key: 'name' },
    {
      header: 'Frequency',
      key: 'frequency',
      valueFormatter: (frequency) => {
        const frequencyType = FREQUENCY_TYPE_TO_SUCCESS_MESSAGE[frequency];
        return capitalize(frequencyType);
      },
    },
    {
      header: 'Trade (-s)',
      key: 'tradelanes',
      valueFormatter: (tradelanes) =>
        tradelanes.map(({ name }) => name).join(', '),
    },
    {
      header: 'Recipients',
      key: 'recipient',
      valueFormatter: ({ email, count }) => {
        if (count > 1) {
          return `${email} (+${count - 1})`;
        }

        return email;
      },
    },
    {
      header: 'Created on',
      key: 'createdAt',
    },
  ],
  actions: (item?: IRecipientLisRow) => {
    return [
      {
        key: 'view',
        icon: 'mdi:eye',
        dataTestId: 'row-view',
        tooltipDescription: 'View',
        variant: 'btn-tertiary',
        to: {
          name: 'AdminScheduleRecipientListShow',
          params: { id: item?.id },
        },
      },
      {
        key: 'edit',
        icon: 'ic:baseline-edit',
        dataTestId: 'row-edit',
        tooltipDescription: 'Edit',
        variant: 'btn-tertiary',
        to: {
          name: 'AdminScheduleRecipientListEdit',
          params: {
            id: item?.id,
          },
        },
        disabled: isSpectator.value,
      },
      {
        key: 'remove',
        icon: 'ic:baseline-delete',
        dataTestId: 'remove',
        tooltipDescription: 'Delete',
        variant: 'btn-tertiary',
        disabled: isSpectator.value,
        handler: async () => {
          const result = await showModal(ModalConfirmDanger, {
            title: `Delete Recipient List “${item?.name}”?`,
            message:
              'The list will be deleted permanently. Are you sure you want to delete it? ',
          });

          if (result.action === 'CONFIRM' && item?.id) {
            Service.recipientList()
              .destroy(item.id)
              .onSuccess(() => {
                snackbar.add({
                  type: 'success',
                  text: 'The recipient list deleted permanently.',
                });

                Service.recipientList()
                  .filters()
                  .onSuccess(({ data }) => {
                    tradelanes.value = data.tradelanes;
                  })
                  .execute();

                serviceState.fetchData();
              })
              .onError(() => {
                snackbar.add({
                  type: 'error',
                  title: 'Oops, something went wrong.',
                  text: 'We could not delete the list. Please try again.',
                });
              })
              .execute();
          }
        },
      },
    ];
  },
};

const onSearchClear = () => {
  query.search = '';
  filtersQuery.trades = [];
};

onMounted(() => {
  Service.recipientList()
    .filters()
    .onSuccess(({ data }) => {
      tradelanes.value = data.tradelanes;
    })
    .execute();
});
</script>

<template>
  <BasePage title="Recipient List">
    <template v-if="!isSpectator" #button>
      <BaseButton
        icon="mdi:plus"
        :to="{ name: 'AdminScheduleRecipientListNew' }"
      >
        New Recipient List
      </BaseButton>
    </template>

    <div class="col-span-full space-y-2">
      <SearchField
        v-if="!serviceState.stateHasErrors"
        placeholder="Search by List Name, Email or Trade"
        v-model="query.search"
      />

      <AdminScheduleRecipientListFilters
        v-if="!serviceState.stateHasErrors"
        v-model="filtersQuery"
        :tradelanes="tradelanes"
      />

      <CustomCard>
        <ServiceCard
          v-bind="serviceState"
          no-data-title="This Is Where You'll See Your Recipient Lists of Shared Sailing Schedule."
          noResultsMessage="Please amend the search criteria or filter options to see available data"
          noDataMessage="Start by making a new list"
          noDataIcon="vessel_with_anchor"
          @clear="onSearchClear"
        >
          <template #noDataAction>
            <BaseButton
              icon="mdi:plus"
              variant="btn-tertiary"
              :to="{
                name: 'AdminScheduleRecipientListNew',
              }"
            >
              New Recipient List
            </BaseButton>
          </template>

          <DataTableLoading
            v-if="serviceState.stateIsLoading"
            :columns="tableSettings.columns.length"
          />

          <DataTable v-else :settings="tableSettings" :data="tableRows">
            <template #actions="{ item }">
              <DataTableActions :actions="tableSettings.actions!(item)" />
            </template>
          </DataTable>
        </ServiceCard>
      </CustomCard>
    </div>
  </BasePage>
</template>
